import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, LogOut, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Bright red strip pinned to the top of the viewport whenever an
 * impersonation token is active. Sits ABOVE the topbar across both
 * AppShell and AdminShell so operators can never lose track of context.
 */
export function ImpersonationBanner() {
    const { user, tenant, exitImpersonation } = useAuth();
    const nav = useNavigate();
    const [exiting, setExiting] = useState(false);

    if (!user || !user.impersonated_by) return null;

    async function onExit() {
        setExiting(true);
        const r = await exitImpersonation();
        setExiting(false);
        if (r.ok) {
            toast.success("Exited impersonation. Back to admin console.");
            nav("/admin", { replace: true });
        } else if (r.requiresRelogin) {
            toast.error("Lost original session. Please sign in again.");
            nav("/login", { replace: true });
        } else {
            toast.error(r.message || "Could not end impersonation cleanly.");
        }
    }

    return (
        <div
            data-testid="impersonation-banner"
            className="sticky top-0 z-50 flex h-10 w-full items-center justify-between gap-3 bg-red-600 px-4 text-white shadow-md"
        >
            <div className="flex items-center gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span data-testid="impersonation-banner-text">
                    You are impersonating{" "}
                    <strong className="font-semibold">{tenant?.name || "tenant"}</strong> as{" "}
                    <strong className="font-semibold">{user.role}</strong>. All actions are logged.
                </span>
            </div>
            <Button
                size="sm"
                variant="secondary"
                onClick={onExit}
                disabled={exiting}
                data-testid="impersonation-exit-btn"
                className="h-7 gap-1.5 border-white/40 bg-white text-red-700 hover:bg-red-50"
            >
                {exiting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogOut className="h-3.5 w-3.5" />}
                Exit Impersonation
            </Button>
        </div>
    );
}
